<template>
  <div>
    <b-card>
      <b-row>
        <!-- Upload Section -->
        <b-col md="12" class="mb-4">
          <div class="border rounded p-3">
            <h4 class="mb-2">House Pin Images</h4>

            <small class="text-muted d-block mb-2">
              (Recommended size: 200x200)
            </small>

            <b-form-file
              ref="refInputEl"
              v-model="input.userFiles"
              accept=".jpg, .png, .gif"
              multiple
              placeholder="Choose images..."
              browse-text="Browse"
              @input="handleImageUpload"
            />
          </div>
        </b-col>

        <!-- List Section -->
        <b-col md="12">
          <h6 class="text-primary font-weight-bold mb-3">
            Image List for House ID #{{ housePinId }}
          </h6>

          <draggable
            v-model="lists"
            tag="ul"
            group="images"
            :options="options"
            handle=".handle"
            class="list-group list-group-flush cursor-move"
            @end="handleDragEnd"
          >
            <b-list-group-item
              v-for="(image, index) in lists"
              :key="image.id || index"
              tag="li"
              class="d-flex align-items-center justify-content-between"
            >
              <div class="d-flex align-items-center">
                <feather-icon
                  class="handle mr-3 text-muted cursor-move"
                  style="font-size: 20px;"
                  icon="MenuIcon"
                />

                <b-img
                  v-if="image.fullfilepath"
                  :src="image.fullfilepath"
                  height="100"
                  width="150"
                  class="rounded border mr-3"
                />

                <span class="text-truncate" style="max-width: 200px;">
                  {{ image.name }}
                </span>
              </div>

              <b-button
                variant="danger"
                size="sm"
                @click="confirmDelete(image)"
              >
                Delete
              </b-button>
            </b-list-group-item>
          </draggable>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import router from '@/router'
import draggable from 'vuedraggable'
import {
  BCard, BRow, BCol, BImg, BFormFile, BButton, BListGroupItem,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
  name: 'HousePinImages',
  components: {
    BCard, BRow, BCol, BImg, BFormFile, BButton, BListGroupItem, draggable,
  },
  data() {
    return {
      housePinId: router.currentRoute.params.id,
      input: {
        userFiles: [],
      },
      previewImages: [],
      lists: [],
      options: {
        animation: 200,
        handle: '.handle',
      },
    }
  },
  mounted() {
    this.fetchImages()
  },
  methods: {
    async fetchImages() {
      try {
        const { data } = await axios.get(`/v1/house-pin/image/${this.housePinId}`)
        if (data?.success && Array.isArray(data.data)) {

          this.lists = data.data.map(img => ({
            id: img.id,
            name: img.name ||  `image-${img.id}`,
            fullfilepath: img.url,
            uploading: false,
          }))
        }
      } catch (err) {
        console.error('Failed to fetch images:', err)
        this.$bvToast.toast('Failed to load images', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async handleImageUpload(files) {
      if (!files || !files.length) return

      Array.from(files).forEach(async (file) => {
        const preview = URL.createObjectURL(file)
        this.previewImages.push(preview)

        // Display preview instantly
        const tempItem = {
          id: null,
          name: file.name,
          fullfilepath: preview,
          file,
          uploading: true,
        }
        this.lists.push(tempItem)

        try {
          // Prepare form data
          const formData = new FormData()
          formData.append('image', file)

          // Send to backend
          const { data } = await axios.post(`/v1/house-pin/image/upload/${this.housePinId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })

          if (data?.success && data?.data) {
            const { id, url } = data.data;

            tempItem.id = id
            tempItem.fullfilepath = url
            tempItem.uploading = false
          }
        } catch (err) {
          console.error('Upload failed:', err)
          tempItem.uploading = false
          this.$bvToast.toast('Failed to upload ' + file.name, {
            title: 'Upload error',
            variant: 'danger',
            solid: true,
          })
        }
      })

      this.$refs.refInputEl.reset()
    },
    async handleDragEnd() {
      console.log('New order:', this.lists)

      const orderedData = this.lists.map((image, index) => ({
        id: image.id,
        order: index + 1
      }))

      try {
        // Send the new order to backend
        await axios.post(`/v1/house-pin/image/reorder/${this.housePinId}`, { images: orderedData })

        this.$bvToast.toast('Image order updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        })
      } catch (error) {
        console.error(error)
        this.$bvToast.toast('Failed to update image order', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        })
      }
    },
    confirmDelete(image) {
      this.$bvModal.msgBoxConfirm(`Are you sure you want to delete ${image.name || 'this image'}?`, {
        title: 'Confirm Deletion',
        okTitle: 'Yes, delete',
        okVariant: 'danger',
        cancelTitle: 'Cancel',
        cancelVariant: 'secondary',
        centered: true
      })
      .then(value => {
        if (value) {
          this.deleteImage(image)
          this.$bvToast.toast('Image deleted successfully', {
            title: 'Deleted',
            variant: 'success',
            solid: true,
            autoHideDelay: 2000
          })
        }
      })
      .catch(err => {
        console.error(err)
      })
    },
    async deleteImage(image) {
      if (image.fullfilepath.startsWith('blob:')) {
        URL.revokeObjectURL(image.fullfilepath)
      }

      this.lists = this.lists.filter(item => item !== image)
      this.previewImages = this.previewImages.filter(preview => preview !== image.fullfilepath)
      
      if (image.id) {
        try {
          await axios.delete(`/v1/house-pin/image/delete/${image.id}`)
        } catch (error) {
          this.$bvToast.toast('Failed to delete image from server', {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 3000
          })
        }
      } else {
        this.$bvToast.toast('Local image removed', {
          title: 'Deleted',
          variant: 'info',
          solid: true,
          autoHideDelay: 2000
        })
      }
    },
  },
}
</script>

<style scoped>
.list-group-item {
  transition: all 0.2s ease;
}
.list-group-item:hover {
  background-color: #f8f9fa;
}
.cursor-move {
  cursor: move;
}
</style>